import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Navigate, useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, KeyRound, Copy, Check, ChevronDown } from "lucide-react";
import { Card, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { useAuth } from "contexts/AuthContext";
import { useI18n } from "i18n";
import api from "lib/api";

// Per-method colours (Swagger-style): solid badge + tinted row/border.
const METHOD_STYLES = {
  GET: { badge: "bg-sky-500", border: "border-sky-500/40", head: "bg-sky-500/[0.06] hover:bg-sky-500/[0.12]" },
  POST: { badge: "bg-emerald-500", border: "border-emerald-500/40", head: "bg-emerald-500/[0.06] hover:bg-emerald-500/[0.12]" },
  PUT: { badge: "bg-amber-500", border: "border-amber-500/40", head: "bg-amber-500/[0.06] hover:bg-amber-500/[0.12]" },
  DELETE: { badge: "bg-red-500", border: "border-red-500/40", head: "bg-red-500/[0.06] hover:bg-red-500/[0.12]" },
};

// Dark, copy-able code block (curl / JSON examples).
function CodeBox({ code }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable */ }
  };
  return (
    <div className="relative group">
      <pre className="rounded-md bg-neutral-900 text-neutral-100 text-xs font-mono px-3 py-2.5 overflow-x-auto whitespace-pre border border-border">{code}</pre>
      <button
        onClick={copy}
        className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] border border-neutral-700 bg-neutral-800 hover:bg-neutral-700 transition-colors text-neutral-200"
        title={t("apiToken.copy")}
      >
        {copied ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
        {copied ? t("apiToken.copied") : t("apiToken.copy")}
      </button>
    </div>
  );
}

// One collapsible Swagger-style operation row.
function Operation({ op }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const s = METHOD_STYLES[op.method] || METHOD_STYLES.POST;
  return (
    <div className={`rounded-lg border ${s.border} overflow-hidden`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${s.head}`}
      >
        <span className={`px-2.5 py-1 rounded text-[11px] font-bold tracking-wide text-white shrink-0 ${s.badge}`}>{op.method}</span>
        <code className="text-sm font-mono font-semibold text-foreground shrink-0">{op.path}</code>
        <span className="text-sm text-muted-foreground truncate">{t(op.summary)}</span>
        <ChevronDown className={`ml-auto h-4 w-4 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-border/60 bg-card px-4 py-4 space-y-5">
          {op.desc && <p className="text-sm text-muted-foreground">{t(op.desc)}</p>}

          {op.params?.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{t("apiToken.parameters")}</h4>
              <div className="rounded-md border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/40 text-muted-foreground text-xs">
                      <th className="text-left px-3 py-2 font-medium w-56">{t("apiToken.paramName")}</th>
                      <th className="text-left px-3 py-2 font-medium">{t("apiToken.paramDesc")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {op.params.map((p) => (
                      <tr key={p.name} className="border-t border-border/60 align-top">
                        <td className="px-3 py-2">
                          <div className="font-mono font-semibold text-foreground">
                            {p.name}
                            {p.required && <span className="text-red-400 text-[11px] ml-1.5">{t("apiToken.required")}</span>}
                          </div>
                          <div className="text-[11px] text-muted-foreground font-mono">
                            {p.type} <span className="italic">({p.in})</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{t(p.desc)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{t("apiToken.request")}</h4>
            <CodeBox code={op.curl} />
          </div>

          {op.responses?.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{t("apiToken.responses")}</h4>
              <div className="space-y-3">
                {op.responses.map((r) => (
                  <div key={r.code} className="flex gap-3">
                    <code className="text-sm font-mono font-semibold text-foreground pt-0.5 w-10 shrink-0">{r.code}</code>
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <p className="text-sm text-muted-foreground">{t(r.desc)}</p>
                      {r.example && <CodeBox code={r.example} />}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BiometricExamples() {
  const { t } = useI18n();
  const base = `${window.location.origin}/biometric`;
  const H = '-H "X-Api-Token: SEU_TOKEN"';
  const tokenParam = { name: "X-Api-Token", type: "string", in: "header", required: true, desc: "apiToken.pToken" };

  const peopleOps = [
    {
      method: "POST", path: "/people/add",
      summary: "biometric.addSummary",
      desc: "biometric.addDesc",
      params: [
        tokenParam,
        { name: "emails", type: "string[]", in: "body", required: false, desc: "biometric.pEmails" },
        { name: "document_type", type: "string", in: "body", required: false, desc: "biometric.pDocumentType" },
        { name: "document", type: "string", in: "body", required: false, desc: "biometric.pDocument" },
        { name: "mobile_phone", type: "string", in: "body", required: false, desc: "biometric.pMobilePhone" },
      ],
      curl: `curl -X POST "${base}/people/add" \\\n  ${H} \\\n  -H "Content-Type: application/json" \\\n  -d '{\n    "emails": ["fulano@exemplo.com"],\n    "document_type": "cpf",\n    "document": "123.456.789-00",\n    "mobile_phone": "+55 11 91234-5678"\n  }'`,
      responses: [{
        code: "201", desc: "biometric.rCreated",
        example: `{\n  "created": true,\n  "person": {\n    "id": "0b6c3c2e-…",\n    "emails": ["fulano@exemplo.com"],\n    "document_type": "cpf",\n    "document": "123.456.789-00",\n    "mobile_phone": "+55 11 91234-5678",\n    "photos": 0\n  }\n}`,
      }, {
        code: "200", desc: "biometric.rUpdated",
        example: `{\n  "created": false,\n  "person": { "id": "0b6c3c2e-…", "photos": 2, "…": "…" }\n}`,
      },
      { code: "400", desc: "biometric.rBadRequest" },
      { code: "401", desc: "apiToken.rUnauthorized" },
      { code: "403", desc: "biometric.rForbidden" },
      { code: "429", desc: "biometric.rRateLimited" }],
    },
    {
      method: "POST", path: "/people/{id}/add_photo",
      summary: "biometric.photoSummary",
      desc: "biometric.photoDesc",
      params: [
        tokenParam,
        { name: "id", type: "uuid", in: "path", required: true, desc: "biometric.pPersonId" },
        { name: "photo", type: "file | base64", in: "body", required: true, desc: "biometric.pPhoto" },
      ],
      curl: `# multipart (arquivo):\ncurl -X POST "${base}/people/0b6c3c2e-…/add_photo" \\\n  ${H} \\\n  -F "photo=@cnh.jpg"\n\n# JSON (base64):\ncurl -X POST "${base}/people/0b6c3c2e-…/add_photo" \\\n  ${H} \\\n  -H "Content-Type: application/json" \\\n  -d '{"photo": "data:image/jpeg;base64,/9j/4AAQ…"}'`,
      responses: [{
        code: "201", desc: "biometric.rPhotoStored",
        example: `{\n  "id": "9f1e77aa-…",\n  "person_id": "0b6c3c2e-…",\n  "det_score": 0.83\n}`,
      },
      { code: "204", desc: "biometric.rPhotoDuplicate" },
      { code: "422", desc: "biometric.rNoFace" },
      { code: "400", desc: "biometric.rInvalidImage" },
      { code: "404", desc: "biometric.rPersonNotFound" }],
    },
  ];

  return (
    <Card className="mt-6">
      <CardContent className="p-5">
        <h2 className="text-lg font-semibold mb-1">{t("biometric.title")}</h2>
        <p className="text-sm text-muted-foreground mb-4">
          {t("biometric.introAuth")} <code className="text-xs font-mono text-foreground bg-muted px-1.5 py-0.5 rounded">X-Api-Token</code> {t("biometric.introAt")}{" "}
          <code className="text-xs font-mono text-foreground bg-muted px-1.5 py-0.5 rounded">{base}</code>.{" "}
          {t("biometric.introScopePre")} <em>{t("biometric.scopeName")}</em> {t("biometric.introScopePost")}
        </p>
        <div className="space-y-2">
          {peopleOps.map((op) => <Operation key={op.path} op={op} />)}
        </div>
      </CardContent>
    </Card>
  );
}

function DeleteConfirmModal({ item, onClose, onConfirm, t }) {
  const [loading, setLoading] = useState(false);
  const del = async () => {
    setLoading(true);
    try { await api.delete(`/api/api-tokens/${item.id}/`); onConfirm(); }
    catch { setLoading(false); }
  };
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-card rounded-2xl shadow-xl w-full max-w-sm mx-4 animate-fade-in border border-border">
        <div className="p-6 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
            <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
          </div>
          <p className="text-sm text-muted-foreground mb-6">
            {t("common.confirmRemove")} <strong>{item.name}</strong>?
          </p>
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
            <Button variant="destructive" onClick={del} loading={loading}>{t("common.remove")}</Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function ApiTokens() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  const fetchItems = useCallback(() => {
    api.get("/api/api-tokens/").then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (user?.capabilities?.manage_settings) fetchItems();
    else setLoading(false);
  }, [fetchItems, user]);

  if (user && !user.capabilities?.manage_settings) {
    return <Navigate to="/liveness/admin/client-apps" replace />;
  }

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("apiToken.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("apiToken.subtitle")}</p>
        </div>
        <Button onClick={() => navigate("/liveness/admin/api-tokens/new")}>
          <Plus className="h-4 w-4 mr-2" />{t("apiToken.new")}
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-12">
              <KeyRound className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">{t("apiToken.empty")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">{t("common.name")}</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">{t("apiToken.ipAcl")}</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">{t("apiToken.rateLimit")}</th>
                    <th className="text-right px-4 py-3 font-medium text-muted-foreground">{t("common.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 font-medium">{it.name}</td>
                      <td className="px-4 py-3 text-muted-foreground">{(it.allowed_ips || []).length ? (it.allowed_ips || []).join(", ") : t("apiToken.anyIp")}</td>
                      <td className="px-4 py-3 text-muted-foreground">{it.rate_limit_per_minute || t("apiToken.noLimit")}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => navigate(`/liveness/admin/api-tokens/${it.id}/edit`)} className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground" title={t("common.edit")}>
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button onClick={() => setDeleting(it)} className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-muted-foreground hover:text-red-600" title={t("common.remove")}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <BiometricExamples />

      {deleting && (
        <DeleteConfirmModal item={deleting} t={t} onClose={() => setDeleting(null)} onConfirm={() => { setDeleting(null); fetchItems(); }} />
      )}
    </div>
  );
}
